import { submissionApiClient } from "@/api/submission-api";
import {
  getUrlValidationError,
  getLinkedInUrlValidationError,
} from "@/lib/url-utils";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { VirtualizedCombobox } from "@/components/ui/virtualized-combobox";
import type { CompanyInfoFormData } from "@/lib/schema/submission-schema";
import {
  BUSINESS_TYPE_MAPPING,
  COMPANY_STAGES,
  COUNTRIES,
} from "@/lib/schema/submission-schema";
import { BusinessModels, GICSSubIndustries, RevenueModels, getStageText } from "@/lib/utils";
import React, { useEffect, useMemo, useState } from "react";

interface CompanyInfoStepProps {
  companyData: any;
  onNext: (companyInfo: CompanyInfoFormData) => void;
  onBack?: () => void;
}

export default function CompanyInfoStep({
  companyData,
  onNext,
  onBack,
}: CompanyInfoStepProps) {
  const [formData, setFormData] = useState<CompanyInfoFormData>({
    companyWebsite: "",
    linkedInUrl: "",
    address: "",
    country: "",
    gicsIndustry: [] as string[],
    gicsSubIndustry: [] as string[],
    businessModel: [],
    revenueModel: [],
    companyStage: "",
    contactPhone: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Threads each option's rich metadata into VirtualizedCombobox, which
  // renders it as an Info-icon hover tooltip next to each option — matches
  // the industry-tag affordance on the deal-info page.
  const industryTagOptions = useMemo(
    () =>
      GICSSubIndustries.map((si) => ({
        value: si.code,
        label: si.name,
        description: si.description,
      })),
    [],
  );

  const businessModelOptions = useMemo(
    () =>
      BusinessModels.map((bm) => ({
        value: bm.value,
        label: bm.label,
        // Flatten the richer metadata (payer / receiver) into the single
        // tooltip line the combobox exposes.
        description: `${bm.notes} • Payer: ${bm.payer} · Receiver: ${bm.receiver}`,
      })),
    [],
  );

  const revenueModelOptions = useMemo(
    () =>
      RevenueModels.map((rm) => {
        const examples =
          rm.examples && rm.examples.length > 0
            ? ` • Examples: ${rm.examples.join(", ")}`
            : "";
        return {
          value: rm.value,
          label: rm.label,
          description: `${rm.description}${examples}`,
        };
      }),
    [],
  );

  useEffect(() => {
    if (companyData && companyData._id) {
      // Business model may arrive as a single string or an array, and each
      // entry may be either the short code ("B2B") or the readable label
      // ("B2B - Business to Business"). Normalise to an array of short codes
      // so it lines up with `BusinessModels[].value` used by MultiSelect.
      const toShortCode = (raw: unknown): string | null => {
        if (typeof raw !== "string" || !raw) return null;
        if (Object.values(BUSINESS_TYPE_MAPPING).includes(raw)) return raw;
        if (BUSINESS_TYPE_MAPPING[raw]) return BUSINESS_TYPE_MAPPING[raw];
        // Some records store just the ticker half of the readable label.
        const match = raw.split("-")[0]?.trim();
        return match || null;
      };
      const rawBusinessModels = Array.isArray(companyData.business_model)
        ? companyData.business_model
        : companyData.business_model
          ? [companyData.business_model]
          : [];
      const businessModelValues = rawBusinessModels
        .map((v: unknown) => toShortCode(v))
        .filter((v: string | null): v is string => !!v);

      const rawRevenueModels = Array.isArray(companyData.revenue_model)
        ? companyData.revenue_model
        : companyData.revenue_model
          ? [companyData.revenue_model]
          : [];
      const revenueModelValues = rawRevenueModels.filter(
        (v: unknown): v is string => typeof v === "string" && v.length > 0,
      );

      const initialData: CompanyInfoFormData = {
        companyWebsite:
          companyData.company_website === "undefined"
            ? ""
            : companyData.company_website || "",
        linkedInUrl:
          companyData.linkedIn === "undefined"
            ? ""
            : companyData.linkedIn || "",
        address: companyData.address || "",
        country: companyData.country || "",
        // Industry tag (L5 sub-industry) — coerce to strings for GICS lookup match
        gicsIndustry: Array.isArray(companyData.gics_industry)
          ? companyData.gics_industry.map(String)
          : companyData.gics_industry
            ? [String(companyData.gics_industry)]
            : companyData.gics_sub_industry
              ? [String(Array.isArray(companyData.gics_sub_industry) ? companyData.gics_sub_industry[0] : companyData.gics_sub_industry).substring(0, 8)]
              : [],
        gicsSubIndustry: Array.isArray(companyData.gics_sub_industry)
          ? companyData.gics_sub_industry.map(String)
          : companyData.gics_sub_industry
            ? [String(companyData.gics_sub_industry)]
            : [],
        // Business & Revenue
        businessModel: businessModelValues,
        revenueModel: revenueModelValues,
        companyStage: companyData.stage ? getStageText(companyData.stage) : "",
        contactPhone:
          companyData.contact_phone === "undefined"
            ? ""
            : companyData.contact_phone || "",
      };

      setFormData(initialData);
    }
  }, [companyData]);

  const handleInputChange = (
    field: keyof CompanyInfoFormData,
    value: string | number
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // Handle industry tag toggle - multi-select with auto-derived parent industry codes
  const handleIndustryTagChange = (subIndustryCode: string) => {
    setFormData((prev) => {
      const currentTags = Array.isArray(prev.gicsSubIndustry) ? prev.gicsSubIndustry : prev.gicsSubIndustry ? [prev.gicsSubIndustry] : [];
      const newTags = currentTags.includes(subIndustryCode)
        ? currentTags.filter((t) => t !== subIndustryCode)
        : [...currentTags, subIndustryCode];
      const newIndustries = [...new Set(newTags.map((code) => code.substring(0, 8)))];
      return {
        ...prev,
        gicsIndustry: newIndustries,
        gicsSubIndustry: newTags,
      };
    });
    if (errors.gicsIndustry) {
      setErrors((prev) => ({ ...prev, gicsIndustry: "" }));
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow digits
    if (/^\d*$/.test(value)) {
      handleInputChange("contactPhone", value);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.address || formData.address.trim().length === 0) {
      newErrors.address = "Address is required";
    }
    if (!formData.country || formData.country.trim().length === 0) {
      newErrors.country = "Country is required";
    }
    const subIndustryTags = Array.isArray(formData.gicsSubIndustry) ? formData.gicsSubIndustry : formData.gicsSubIndustry ? [formData.gicsSubIndustry] : [];
    if (subIndustryTags.length === 0) {
      newErrors.gicsIndustry = "Industry tag is required";
    }
    if (!formData.businessModel || formData.businessModel.length === 0) {
      newErrors.businessModel = "Business model is required";
    }
    if (!formData.companyStage) {
      newErrors.companyStage = "Company stage is required";
    }

    const websiteError = getUrlValidationError(formData.companyWebsite ?? "");
    if (websiteError) newErrors.companyWebsite = websiteError;

    const linkedInError = getLinkedInUrlValidationError(
      formData.linkedInUrl ?? ""
    );
    if (linkedInError) newErrors.linkedInUrl = linkedInError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const response = await submissionApiClient.company.updateCompany(
        companyData._id,
        formData
      );
      console.log("Company updated successfully", response);

      onNext(formData);
    } catch (error) {
      console.error("Error updating company:", error);
      setErrors({
        submit: "Failed to update company information. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-3 py-2 md:p-6">
      <Card>
        <CardHeader className="px-4 py-4 md:px-6 md:py-6">
          <CardTitle className="text-lg md:text-xl">Company Information</CardTitle>
          <CardDescription>
            Tell us more about your company. Fields marked with * are required.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5 md:space-y-6 px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* Company Website */}
            <div>
              <Label htmlFor="companyWebsite">Company Website</Label>
              <Input
                id="companyWebsite"
                type="url"
                value={formData.companyWebsite}
                onChange={(e) =>
                  handleInputChange("companyWebsite", e.target.value)
                }
                onBlur={(e) => {
                  const err = getUrlValidationError(e.target.value);
                  setErrors((prev) => ({
                    ...prev,
                    companyWebsite: err ?? "",
                  }));
                }}
                placeholder="https://example.com"
              />
              {errors.companyWebsite && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.companyWebsite}
                </p>
              )}
            </div>

            {/* Contact Phone */}
            <div>
              <Label htmlFor="contactPhone">Contact Phone</Label>
              <Input
                id="contactPhone"
                value={formData.contactPhone}
                onChange={handlePhoneChange}
                placeholder="1234567890"
              />
              {errors.contactPhone && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.contactPhone}
                </p>
              )}
            </div>

            {/* LinkedIn URL */}
            <div>
              <Label htmlFor="linkedInUrl">LinkedIn URL</Label>
              <Input
                id="linkedInUrl"
                type="url"
                value={formData.linkedInUrl}
                onChange={(e) =>
                  handleInputChange("linkedInUrl", e.target.value)
                }
                onBlur={(e) => {
                  const err = getLinkedInUrlValidationError(e.target.value);
                  setErrors((prev) => ({
                    ...prev,
                    linkedInUrl: err ?? "",
                  }));
                }}
                placeholder="https://linkedin.com/company/example"
              />
              {errors.linkedInUrl && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.linkedInUrl}
                </p>
              )}
            </div>

            {/* Address */}
            <div>
              <Label htmlFor="address">Address *</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
                placeholder="Enter your address or city"
              />
              {errors.address && (
                <p className="text-sm text-red-600 mt-1">{errors.address}</p>
              )}
            </div>

            {/* Country */}
            <div>
              <Label htmlFor="country">Country *</Label>
              <Select
                value={formData.country}
                onValueChange={(value) => handleInputChange("country", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((country) => (
                    <SelectItem key={country} value={country}>
                      {country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.country && (
                <p className="text-sm text-red-600 mt-1">{errors.country}</p>
              )}
            </div>

            {/* Empty cell for grid alignment */}
            <div className="hidden md:block" />
          </div>

          {/* Industry Tag */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="industryTag">Industry Tag *</Label>
              <VirtualizedCombobox
                options={industryTagOptions}
                selected={Array.isArray(formData.gicsSubIndustry) ? formData.gicsSubIndustry : formData.gicsSubIndustry ? [formData.gicsSubIndustry] : []}
                onToggle={(code) => handleIndustryTagChange(code)}
                placeholder="Select industry tags"
                searchPlaceholder="Search industry tags..."
                multiple={true}
              />
              {errors.gicsIndustry && (
                <p className="text-sm text-red-600 mt-1">{errors.gicsIndustry}</p>
              )}
            </div>
          </div>

          {/* Business Model & Revenue Model */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* Business Model */}
            <div>
              <Label htmlFor="businessModel">Business Model *</Label>
              <VirtualizedCombobox
                options={businessModelOptions}
                selected={formData.businessModel}
                onToggle={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    businessModel: prev.businessModel.includes(value)
                      ? prev.businessModel.filter((v) => v !== value)
                      : [...prev.businessModel, value],
                  }));
                  if (errors.businessModel) {
                    setErrors((prev) => ({ ...prev, businessModel: "" }));
                  }
                }}
                placeholder="Select business models"
                searchPlaceholder="Search business models..."
                multiple
              />
              {errors.businessModel && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.businessModel}
                </p>
              )}
            </div>

            {/* Revenue Model */}
            <div>
              <Label htmlFor="revenueModel">Revenue Model</Label>
              <VirtualizedCombobox
                options={revenueModelOptions}
                selected={formData.revenueModel ?? []}
                onToggle={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    revenueModel: (prev.revenueModel ?? []).includes(value)
                      ? (prev.revenueModel ?? []).filter((v) => v !== value)
                      : [...(prev.revenueModel ?? []), value],
                  }))
                }
                placeholder="Select revenue models"
                searchPlaceholder="Search revenue models..."
                multiple
              />
            </div>

            {/* Company Stage */}
            <div>
              <Label htmlFor="companyStage">Company Stage *</Label>
              <Select
                value={formData.companyStage}
                onValueChange={(value) =>
                  handleInputChange("companyStage", value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company stage" />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_STAGES.map((stage) => (
                    <SelectItem key={stage.value} value={String(stage.value)}>
                      {stage.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.companyStage && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.companyStage}
                </p>
              )}
            </div>
          </div>

          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex pt-6 pb-2 mt-4 border-t border-gray-100">
            {onBack && (
              <Button type="button" variant="outline" onClick={onBack}>
                Back
              </Button>
            )}
            <Button onClick={handleSubmit} disabled={isSubmitting} className="ml-auto">
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </div>
              ) : (
                "Continue"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
